import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormLabel,
  Divider,
  InputAdornment,
  Alert,
  Stepper,
  Step,
  StepLabel,
  useTheme
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  LocalOffer as OfferIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
} from "@mui/icons-material";

const CreatePromotionModal = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Basic Information
    name: "",
    code: "",
    description: "",
    type: "percentage",
    discountValue: "",
    minOrderAmount: "",
    maxDiscountAmount: "",
    
    // Usage Limits
    maxUses: "",
    usesPerCustomer: 1,
    firstTimeOnly: false,
    
    // Timing
    startDate: "",
    endDate: "",
    neverExpires: false,
    
    // Audience
    audience: "all",
    customerGroups: [],
    specificCustomers: [],
    
    // Service Restrictions
    applicableServices: "all",
    specificServices: [],
    
    // Status
    status: "active"
  });

  const [errors, setErrors] = useState({});

  const steps = ['Basic Info', 'Rules & Limits', 'Audience & Timing', 'Review'];

  const discountTypes = [
    { value: "percentage", label: "Percentage Off", icon: <PercentIcon /> },
    { value: "fixed", label: "Fixed Amount", icon: <MoneyIcon /> },
    { value: "free_shipping", label: "Free Shipping", icon: <OfferIcon /> },
  ];

  const audienceTypes = [
    { value: "all", label: "All Customers" },
    { value: "new", label: "New Customers Only" },
    { value: "existing", label: "Existing Customers Only" },
    { value: "groups", label: "Specific Customer Groups" },
    { value: "specific", label: "Specific Customers" },
  ];

  const serviceTypes = [
    { value: "all", label: "All Services" },
    { value: "specific", label: "Specific Services" },
    { value: "category", label: "Service Categories" },
  ];

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const generatePromoCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({
      ...prev,
      code: code
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.name.trim()) newErrors.name = "Promotion name is required";
      if (!formData.code.trim()) newErrors.code = "Promotion code is required";
      if (!formData.discountValue) newErrors.discountValue = "Discount value is required";
      if (formData.type === "percentage" && (formData.discountValue < 1 || formData.discountValue > 100)) {
        newErrors.discountValue = "Percentage must be between 1 and 100";
      }
    }

    if (step === 1) {
      if (formData.maxUses && formData.maxUses < 1) {
        newErrors.maxUses = "Maximum uses must be at least 1";
      }
      if (formData.usesPerCustomer < 1) {
        newErrors.usesPerCustomer = "Uses per customer must be at least 1";
      }
    }

    if (step === 2) {
      if (!formData.startDate && !formData.neverExpires) {
        newErrors.startDate = "Start date is required";
      }
      if (!formData.endDate && !formData.neverExpires) {
        newErrors.endDate = "End date is required";
      }
      if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
        newErrors.endDate = "End date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSave = () => {
    if (validateStep(activeStep)) {
      onSave(formData);
      onClose();
      setActiveStep(0);
      setFormData({
        name: "",
        code: "",
        description: "",
        type: "percentage",
        discountValue: "",
        minOrderAmount: "",
        maxDiscountAmount: "",
        maxUses: "",
        usesPerCustomer: 1,
        firstTimeOnly: false,
        startDate: "",
        endDate: "",
        neverExpires: false,
        audience: "all",
        customerGroups: [],
        specificCustomers: [],
        applicableServices: "all",
        specificServices: [],
        status: "active"
      });
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Promotion Name"
                value={formData.name}
                onChange={handleChange('name')}
                error={!!errors.name}
                helperText={errors.name || "Give your promotion a clear, descriptive name"}
                required
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Promo Code"
                value={formData.code}
                onChange={handleChange('code')}
                error={!!errors.code}
                helperText={errors.code || "Customers will use this code at checkout"}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <OfferIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={generatePromoCode}
                sx={{ height: '56px' }}
              >
                Generate Code
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={3}
                helperText="Describe what this promotion offers to customers"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Discount Type</FormLabel>
                <RadioGroup
                  row
                  value={formData.type}
                  onChange={handleChange('type')}
                >
                  {discountTypes.map((type) => (
                    <FormControlLabel
                      key={type.value}
                      value={type.value}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={
                  formData.type === "percentage" 
                    ? "Discount Percentage" 
                    : formData.type === "fixed"
                    ? "Discount Amount"
                    : "Minimum Order for Free Shipping"
                }
                type="number"
                value={formData.discountValue}
                onChange={handleChange('discountValue')}
                error={!!errors.discountValue}
                helperText={errors.discountValue}
                required
                InputProps={{
                  endAdornment: formData.type === "percentage" ? (
                    <InputAdornment position="end">%</InputAdornment>
                  ) : formData.type === "fixed" ? (
                    <InputAdornment position="start">$</InputAdornment>
                  ) : null,
                }}
              />
            </Grid>

            {formData.type !== "free_shipping" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Minimum Order Amount"
                  type="number"
                  value={formData.minOrderAmount}
                  onChange={handleChange('minOrderAmount')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  helperText="Leave empty for no minimum"
                />
              </Grid>
            )}

            {formData.type === "percentage" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Maximum Discount Amount"
                  type="number"
                  value={formData.maxDiscountAmount}
                  onChange={handleChange('maxDiscountAmount')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  helperText="Limit the maximum discount amount (optional)"
                />
              </Grid>
            )}
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum Uses"
                type="number"
                value={formData.maxUses}
                onChange={handleChange('maxUses')}
                error={!!errors.maxUses}
                helperText={errors.maxUses || "Total times this promo can be used. Leave empty for unlimited."}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Uses Per Customer"
                type="number"
                value={formData.usesPerCustomer}
                onChange={handleChange('usesPerCustomer')}
                error={!!errors.usesPerCustomer}
                helperText={errors.usesPerCustomer || "How many times each customer can use this promo"}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.firstTimeOnly}
                    onChange={handleSwitchChange('firstTimeOnly')}
                    color="primary"
                  />
                }
                label="For first-time customers only"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Service Restrictions
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Applicable Services</InputLabel>
                <Select
                  value={formData.applicableServices}
                  label="Applicable Services"
                  onChange={handleChange('applicableServices')}
                >
                  {serviceTypes.map((service) => (
                    <MenuItem key={service.value} value={service.value}>
                      {service.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {formData.applicableServices === 'specific' && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Specific service selection would be implemented here with a multi-select component.
                </Alert>
              </Grid>
            )}
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={formData.audience}
                  label="Target Audience"
                  onChange={handleChange('audience')}
                >
                  {audienceTypes.map((audience) => (
                    <MenuItem key={audience.value} value={audience.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon fontSize="small" />
                        {audience.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {(formData.audience === 'groups' || formData.audience === 'specific') && (
              <Grid item xs={12}>
                <Alert severity="info">
                  {formData.audience === 'groups' 
                    ? "Customer group selection would be implemented here." 
                    : "Specific customer selection would be implemented here."}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Schedule
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.neverExpires}
                    onChange={handleSwitchChange('neverExpires')}
                    color="primary"
                  />
                }
                label="Never expires"
              />
            </Grid>

            {!formData.neverExpires && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={handleChange('startDate')}
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={handleChange('endDate')}
                    error={!!errors.endDate}
                    helperText={errors.endDate}
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        );

      case 3:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Review your promotion details before creating.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Promotion Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.name || "Not set"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Promo Code
                </Typography>
                <Chip label={formData.code || "Not set"} color="primary" />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.description || "No description"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Discount Type
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {discountTypes.find(t => t.value === formData.type)?.label}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Discount Value
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.type === "percentage" 
                    ? `${formData.discountValue}%` 
                    : formData.type === "fixed"
                    ? `$${formData.discountValue}`
                    : "Free Shipping"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Minimum Order
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.minOrderAmount ? `$${formData.minOrderAmount}` : "No minimum"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Maximum Uses
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.maxUses || "Unlimited"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Target Audience
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {audienceTypes.find(a => a.value === formData.audience)?.label}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Validity
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.neverExpires ? "Never expires" : (
                    formData.startDate && formData.endDate 
                      ? `${new Date(formData.startDate).toLocaleDateString()} - ${new Date(formData.endDate).toLocaleDateString()}`
                      : "Not set"
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const handleClose = () => {
    onClose();
    setActiveStep(0);
    setErrors({});
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Create New Promotion
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set up discount codes and special offers for your customers
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ 
        borderTop: `1px solid ${theme.palette.divider}`,
        px: 3,
        py: 2
      }}>
        <Button
          onClick={handleClose}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            sx={{ borderRadius: 2 }}
          >
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{ borderRadius: 2 }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{ borderRadius: 2 }}
          >
            Create Promotion
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreatePromotionModal;